// Telegram Login (OIDC): oauth.telegram.org/js/telegram-login.js opens a popup and returns an
// id_token, which the API verifies. The bot's Login Widget must list this site in Allowed URLs.
import type { AdminLoginConfig, AdminLoginResponse } from '@fonus/shared';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { ErrorCard } from '../components/ErrorCard.tsx';
import { Button } from '../components/ui/button.tsx';
import { Card, CardContent } from '../components/ui/card.tsx';
import { api, ApiFailure, tokenStore } from '../lib/api.ts';
import { humanFromApi, type HumanError } from '../lib/errors.ts';

const SCRIPT = 'https://oauth.telegram.org/js/telegram-login.js?6';
interface TelegramLogin {
  auth: (
    opts: { client_id: number; nonce?: string; lang?: string },
    cb: (r: { id_token?: string; error?: string }) => void,
  ) => void;
}
declare global {
  interface Window {
    Telegram?: { Login?: TelegramLogin };
  }
}
function loadScript(): Promise<TelegramLogin> {
  return new Promise((resolve, reject) => {
    if (window.Telegram?.Login) {
      resolve(window.Telegram.Login);
      return;
    }
    const s = document.createElement('script');
    s.src = SCRIPT;
    s.async = true;
    s.onload = () =>
      window.Telegram?.Login
        ? resolve(window.Telegram.Login)
        : reject(new Error('Telegram Login не загрузился'));
    s.onerror = () => reject(new Error('Не удалось загрузить Telegram Login'));
    document.head.appendChild(s);
  });
}
/** `redirect_uri` the library sends; must be in @BotFather → Login Widget → Allowed URLs. */
const redirectUri = () => window.location.origin + window.location.pathname;
const isPreviewHost = () => /^[0-9a-f]{8}\./.test(window.location.hostname);

export function Login() {
  const cfg = useQuery({
    queryKey: ['login-config'],
    queryFn: () => api<AdminLoginConfig>('/admin/auth/config'),
    retry: false,
  });
  const [error, setError] = useState<HumanError | null>(null);
  const [busy, setBusy] = useState(false);
  const [devId, setDevId] = useState('');

  const signIn = async () => {
    setBusy(true);
    setError(null);
    try {
      const login = await loadScript();
      const c = await api<AdminLoginConfig>('/admin/auth/config');
      const idToken = await new Promise<string>((resolve, reject) => {
        login.auth({ client_id: c.clientId, nonce: c.nonce, lang: 'ru' }, (r) =>
          r.id_token ? resolve(r.id_token) : reject(new Error(r.error ?? 'Вход отменён')),
        );
      });
      const res = await api<AdminLoginResponse>('/admin/auth/telegram', {
        method: 'POST',
        body: { idToken, nonceToken: c.nonceToken },
      });
      tokenStore.set(res.token);
    } catch (e) {
      if (e instanceof ApiFailure) {
        const h = humanFromApi(e);
        setError(
          e.status === 403
            ? {
                ...h,
                title: 'Вы не администратор',
                why: 'Этот Telegram-аккаунт не добавлен в админы.',
                todo: 'Попросите владельца добавить вас.',
              }
            : h,
        );
      } else {
        // Telegram's own popup failed (closed, origin / redirect_uri not allowed…).
        setError({
          title: e instanceof Error ? e.message : 'Вход не удался',
          why: 'Если окно Telegram пишет «origin required» или «redirect_uri required» — адрес не добавлен в настройки бота.',
          todo: `В @BotFather → бот → Login Widget → Allowed URLs должен быть ровно ${redirectUri()}`,
          technical: {
            code: null,
            requestId: null,
            text: e instanceof Error ? e.message : String(e),
          },
        });
      }
    } finally {
      setBusy(false);
    }
  };
  const devSignIn = async () => {
    try {
      const res = await api<AdminLoginResponse>('/admin/auth/dev', {
        method: 'POST',
        body: { telegramId: devId.trim() },
      });
      tokenStore.set(res.token);
    } catch (e) {
      setError(humanFromApi(e));
    }
  };

  return (
    <main className="mx-auto mt-[14vh] flex max-w-md flex-col gap-4 px-4">
      <Card>
        <CardContent className="flex flex-col items-center gap-3 pt-8 pb-8 text-center">
          <div className="flex size-14 items-center justify-center rounded-[18px] bg-accent text-[26px] text-white">
            🌙
          </div>
          <h1 className="text-[24px] font-bold">Fonus Ertak · админка</h1>
          <p className="text-[15px] text-muted">Вход для администраторов через Telegram.</p>
          <Button size="lg" disabled={busy} onClick={() => void signIn()}>
            {busy ? 'Входим…' : 'Войти через Telegram'}
          </Button>
          {isPreviewHost() && (
            <p className="text-[13px] text-warn">
              Это адрес предпросмотра Pages — вход работает только на основном адресе админки.
            </p>
          )}
          {cfg.data?.devLogin && (
            <div className="mt-4 flex w-full flex-col gap-2 rounded-[14px] border border-dashed border-line p-3 text-left">
              <span className="text-[12px] text-muted">
                Локальная проверка (только на этом компьютере)
              </span>
              <div className="flex gap-2">
                <input
                  aria-label="Telegram ID"
                  className="h-10 flex-1 rounded-[12px] border border-line bg-card px-3"
                  value={devId}
                  onChange={(e) => setDevId(e.target.value)}
                  placeholder="Telegram ID"
                />
                <Button variant="secondary" onClick={() => void devSignIn()}>
                  Войти
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      {error && <ErrorCard error={error} />}
    </main>
  );
}
